import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { erpIntegrationService } from '../../../services/erpIntegrationService';

const FieldMappingInterface = ({ connection }) => {
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({
    entityType: 'employees',
    hrFieldName: '',
    erpFieldName: '',
    dataType: 'string',
    isRequired: false
  });

  useEffect(() => {
    if (connection?.id) {
      loadMappings();
    }
  }, [connection]);

  const loadMappings = async () => {
    try {
      setLoading(true);
      const data = await erpIntegrationService?.getFieldMappings(connection?.id);
      setMappings(data);
    } catch (error) {
      console.error('Error loading field mappings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMapping = async (e) => {
    e?.preventDefault();
    try {
      await erpIntegrationService?.createFieldMapping({
        connectionConfigId: connection?.id,
        ...formData
      });
      setShowAddModal(false);
      setFormData({
        entityType: 'employees',
        hrFieldName: '',
        erpFieldName: '',
        dataType: 'string',
        isRequired: false
      });
      loadMappings();
    } catch (error) {
      console.error('Error creating field mapping:', error);
    }
  };

  const entityTypes = [
    { value: 'employees', label: 'Employees' },
    { value: 'payroll', label: 'Payroll' },
    { value: 'chart_of_accounts', label: 'Chart of Accounts' },
    { value: 'departments', label: 'Departments' },
    { value: 'cost_centers', label: 'Cost Centers' },
    { value: 'leave_balances', label: 'Leave Balances' }
  ];

  const groupedMappings = mappings?.reduce((acc, mapping) => {
    const entity = mapping?.entityType;
    if (!acc?.[entity]) acc[entity] = [];
    acc?.[entity]?.push(mapping);
    return acc;
  }, {});

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Field Mapping Configuration</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Map HR system fields to {erpIntegrationService?.getERPSystemLabel(connection?.erpSystem)} fields
          </p>
        </div>
        <Button onClick={() => setShowAddModal(true)}>
          <Icon name="Plus" size={16} />
          Add Mapping
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading field mappings...</p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object?.entries(groupedMappings)?.map(([entityType, entityMappings]) => (
            <div key={entityType} className="bg-background border border-border rounded-lg p-5">
              <h3 className="text-lg font-semibold text-foreground mb-4 capitalize">
                {entityType?.replace('_', ' ')}
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">HR Field</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">
                        <Icon name="ArrowRight" size={16} className="mx-auto" />
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">ERP Field</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Data Type</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Required</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entityMappings?.map((mapping) => (
                      <tr key={mapping?.id} className="border-b border-border last:border-0">
                        <td className="py-3 px-4">
                          <span className="text-sm font-medium text-foreground">{mapping?.hrFieldName}</span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <Icon name="ArrowRight" size={14} className="text-muted-foreground mx-auto" />
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-sm font-medium text-foreground">{mapping?.erpFieldName}</span>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-xs px-2 py-1 bg-muted text-muted-foreground rounded">
                            {mapping?.dataType}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          {mapping?.isRequired ? (
                            <Icon name="CheckCircle" size={16} className="text-success mx-auto" />
                          ) : (
                            <Icon name="Circle" size={16} className="text-muted-foreground mx-auto" />
                          )}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <button className="text-muted-foreground hover:text-foreground">
                            <Icon name="Settings" size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Mapping Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg max-w-lg w-full">
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-foreground">Add Field Mapping</h2>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <Icon name="X" size={20} />
                </button>
              </div>
            </div>

            <form onSubmit={handleCreateMapping} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Entity Type *
                </label>
                <Select
                  value={formData?.entityType}
                  onChange={(e) => setFormData({ ...formData, entityType: e?.target?.value })}
                  required
                >
                  {entityTypes?.map((type) => (
                    <option key={type?.value} value={type?.value}>
                      {type?.label}
                    </option>
                  ))}
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  HR System Field Name *
                </label>
                <Input
                  type="text"
                  value={formData?.hrFieldName}
                  onChange={(e) => setFormData({ ...formData, hrFieldName: e?.target?.value })}
                  placeholder="e.g., employee_number"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  ERP System Field Name *
                </label>
                <Input
                  type="text"
                  value={formData?.erpFieldName}
                  onChange={(e) => setFormData({ ...formData, erpFieldName: e?.target?.value })}
                  placeholder="e.g., staff_id"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Data Type *
                </label>
                <Select
                  value={formData?.dataType}
                  onChange={(e) => setFormData({ ...formData, dataType: e?.target?.value })}
                  required
                >
                  <option value="string">String</option>
                  <option value="number">Number</option>
                  <option value="date">Date</option>
                  <option value="boolean">Boolean</option>
                  <option value="json">JSON</option>
                </Select>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isRequired"
                  checked={formData?.isRequired}
                  onChange={(e) => setFormData({ ...formData, isRequired: e?.target?.checked })}
                  className="rounded border-border"
                />
                <label htmlFor="isRequired" className="text-sm text-foreground">
                  Required field
                </label>
              </div>

              <div className="flex items-center gap-3 pt-4">
                <Button type="submit">Create Mapping</Button>
                <Button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="bg-muted text-foreground hover:bg-muted/80"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FieldMappingInterface;
